// API service for patient management
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const appendListFields = (url, fields) => {
  if (!Array.isArray(fields) || fields.length === 0) {
    return url;
  }

  return `${url}&fields=${encodeURIComponent(fields.join(','))}`;
};

class PatientService {
  static async getAllPatients(token, limit = 500, lastKey = null, fields = null) {
    try {
      let url = `${API_BASE_URL}/patients?limit=${limit}`;
      if (lastKey) {
        url += `&lastKey=${encodeURIComponent(JSON.stringify(lastKey))}`;
      }

      url = appendListFields(url, fields);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return {
        patients: data.data,
        pagination: data.pagination
      };
    } catch (error) {
      console.error('Error fetching patients:', error);
      throw error;
    }
  }

  static async getDeletedPatients(token, limit = 500, lastKey = null, fields = null) {
    try {
      let url = `${API_BASE_URL}/patients/deleted?limit=${limit}`;
      if (lastKey) {
        url += `&lastKey=${encodeURIComponent(JSON.stringify(lastKey))}`;
      }

      url = appendListFields(url, fields);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return {
        patients: data.data,
        pagination: data.pagination
      };
    } catch (error) {
      console.error('Error fetching deleted patients:', error);
      throw error;
    }
  }

  static async getAllPatientsAllPages(token, limit = 500, fields = null) {
    try {
      let allPatients = [];
      let lastKey = null;

      do {
        const result = await PatientService.getAllPatients(token, limit, lastKey, fields);
        const pagePatients = result.patients || [];
        allPatients = allPatients.concat(pagePatients);
        lastKey = result.pagination?.lastEvaluatedKey || result.pagination?.lastKey || null;
      } while (lastKey);

      return {
        patients: allPatients
      };
    } catch (error) {
      console.error('Error fetching all patients:', error);
      throw error;
    }
  }

  static async getDeletedPatientsAllPages(token, limit = 500, fields = null) {
    try {
      let allPatients = [];
      let lastKey = null;

      do {
        const result = await PatientService.getDeletedPatients(token, limit, lastKey, fields);
        const pagePatients = result.patients || [];
        allPatients = allPatients.concat(pagePatients);
        lastKey = result.pagination?.lastEvaluatedKey || result.pagination?.lastKey || null;
      } while (lastKey);

      return {
        patients: allPatients
      };
    } catch (error) {
      console.error('Error fetching all deleted patients:', error);
      throw error;
    }
  }

  static async getPatientById(patientId, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/patients/${patientId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error fetching patient:', error);
      throw error;
    }
  }

  static async createPatient(patientData, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/patients`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(patientData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // If we have validation details, format them nicely
        if (errorData.details && Array.isArray(errorData.details)) {
          const fieldErrors = errorData.details.map(detail => `${detail.message}`).join('. ');
          throw new Error(`${errorData.message || 'Error de validación'}: ${fieldErrors}`);
        }
        
        // Use the specific error message from the server
        throw new Error(errorData.message || errorData.error || `Error HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error creating patient:', error);
      throw error;
    }
  }

  static async updatePatient(patientId, patientData, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/patients/${patientId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(patientData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // If we have validation details, format them nicely
        if (errorData.details && Array.isArray(errorData.details)) {
          const fieldErrors = errorData.details.map(detail => `${detail.message}`).join('. ');
          throw new Error(`${errorData.message || 'Error de validación'}: ${fieldErrors}`);
        }
        
        // Use the specific error message from the server
        throw new Error(errorData.message || errorData.error || `Error HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error updating patient:', error);
      throw error;
    }
  }

  static async deletePatient(patientId, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/patients/${patientId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error deleting patient:', error);
      throw error;
    }
  }

  static async restorePatient(patientId, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/patients/${patientId}/restore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error restoring patient:', error);
      throw error;
    }
  }



  static async getPatientStats(token) {
    try {
      const response = await fetch(`${API_BASE_URL}/patients/stats`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error fetching patient stats:', error);
      throw error;
    }
  }
}

export default PatientService; 